import { ReactNode } from "react";
import moment from "moment";
import {
  ABTestPublishedStatus,
  AutomationStatus,
  CommunicationActivityStatus,
  CommunicationPublishedStatus,
  StandardStatus,
  SubscriptionGDPRStatus,
} from "../config/enums";
import { isLocalUserDateField, shouldMaintainEnteredDateTime } from "./utcDateFields";
import { convertUTCDateTimeToLocal } from "../blocks/fields/formatDate";
import { badgeClassName } from "../elements/badgeClassName";
import { convertDayFormatForMoment } from "../utils/utility";
import { SearchColumn } from "./advancedSearch.types";

type Enum = Record<string, number>;

interface StatusBadgeRule {
  list: Enum;
  /** The lowest value that shows a badge: some enums use 0 for "not set". */
  min: number;
  /** The Bootstrap colour of each status; any other is "info". */
  colours: Record<string, string>;
}

const StatusBadges: StatusBadgeRule[] = [
  { list: ABTestPublishedStatus, min: 1, colours: { Published: "success" } },
  { list: AutomationStatus, min: 0, colours: { Published: "success", Testing: "warning" } },
  {
    list: CommunicationActivityStatus,
    min: 0,
    colours: { Completed: "success", Cancelled: "danger", Scheduled: "warning" },
  },
  {
    list: CommunicationPublishedStatus,
    min: 1,
    colours: { Published: "success", Inactive: "danger", Testing: "warning" },
  },
  { list: StandardStatus, min: 0, colours: { Active: "success" } },
  { list: SubscriptionGDPRStatus, min: 0, colours: { Subscribe: "success", Unsubscribe: "danger" } },
];

function statusBadge(list: Enum | undefined, value: number) {
  const rule = StatusBadges.find((candidate) => candidate.list === list);
  const text =
    rule && value !== null && value !== undefined && value >= rule.min
      ? Object.keys(rule.list).find((key) => rule.list[key] === value)
      : undefined;

  return rule && text ? <span className={`${badgeClassName(rule.colours[text] ?? "info")} me-2`}>{text}</span> : "";
}

type DateMode = "default" | "local" | "maintain";

function dateModeOf(entityName: string, field?: SearchColumn): DateMode {
  if (!field?.name) {
    return "default";
  }

  if (field.convertToLocalTime) {
    return "local";
  }

  if (field.maintainEnteredDateTime) {
    return "maintain";
  }

  if (!entityName) {
    return "default";
  }

  if (isLocalUserDateField(entityName, field.name)) {
    return "local";
  }

  return shouldMaintainEnteredDateTime(entityName, field.name) ? "maintain" : "default";
}

const typeOfValue = (value: unknown) => {
  switch (typeof value) {
    case "number":
      return "number";
    case "boolean":
      return "boolean";
    case "string":
      return "string";
    case "object":
      return Array.isArray(value) ? "array" : "object";
    default:
      return "";
  }
};

const yesNo = (value: unknown) => (value === true ? "Yes" : "No");

const localDateTime = (date: unknown) =>
  date === 0 || date === undefined
    ? ""
    : new Date(date as string).toLocaleDateString("en-GB", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      });

function displayDateTime(value: string, field: SearchColumn | undefined, mode: DateMode) {
  const format = field?.dateFormat as string | undefined;
  const entered = moment.utc(value);

  if (format) {
    const shown = mode === "local" ? convertUTCDateTimeToLocal(value) : mode === "maintain" ? entered : moment(value);

    return shown?.format(convertDayFormatForMoment(format)) ?? "";
  }

  if (mode === "maintain") {
    return entered.format("DD/MM/YYYY HH:mm:ss");
  }

  return localDateTime(mode === "local" ? convertUTCDateTimeToLocal(value)?.toDate() : value);
}

/** What a column's value reads as in a table cell: the column's own formatter, else by its type. */
export function displayFieldValue(
  value: unknown,
  column: SearchColumn,
  record: unknown,
  entityName: string
): ReactNode {
  const custom = column.displayFieldValue as ((value: unknown, record: unknown) => ReactNode) | undefined;

  if (custom) {
    return custom(value, record);
  }

  if (value === null || value === undefined) {
    return "";
  }

  const type = column.type || typeOfValue(value);

  switch (type) {
    case "int":
      return (value as number).toLocaleString("en-GB", { useGrouping: true });
    case "money": {
      const amount = Number(value);

      return Number.isNaN(amount)
        ? ""
        : amount.toLocaleString("en-GB", {
            style: "currency",
            currency: "GBP",
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          });
    }
    case "decimal":
    case "number":
      return (value as number).toLocaleString("en-GB", {
        useGrouping: true,
        minimumFractionDigits: 2,
        maximumFractionDigits: 4,
      });
    case "bit":
    case "boolean":
      return column.inverted ? yesNo(!value) : yesNo(value);
    case "select":
    case "string":
    case "text":
      return value as string;
    case "object":
    case "picklist": {
      const named = (value as { Name?: unknown }).Name;
      const list = column.enum as Enum | undefined;

      if (named !== undefined && String(named)) {
        return String(named);
      }

      return list ? Object.keys(list).find((key) => String(list[key]) === String(value)) ?? "" : "";
    }
    case "statusbadge":
      return statusBadge(column.enum as Enum | undefined, value as number);
    case "datetime":
      return value ? displayDateTime(value as string, column, dateModeOf(entityName, column)) : "";
    default:
      return "";
  }
}

export default displayFieldValue;
